import React, { useState, useEffect, useRef, useMemo } from "react";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";

import {
  doc,
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  limit,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import UserContext from "../assets/user_context";
import { tracedCallable } from "../utilities/tracedCallable";
import {
  canRunPredictions,
  LOOKUP_PREDICTION_MIN_COUNT,
} from "../utilities/prediction_utils";
import { isUsableFund } from "../utilities/general_util";
import { useAuth } from "./use-auth";
import { computeEffectiveOrgRoles } from "./orgRoles";
import { TransactionHookRegistryProvider } from "./hooks/transaction_registry";
import ThemeUserSync from "../theme/ThemeUserSync";

// Import race condition tester for development
if (import.meta.env.DEV) {
  import("../utilities/race_condition_tester.js");
}

//Logic flow is import here to manage whether all the user and org's data is loaded or not.
// A user that is loaded but not onboarded will have no orgs, and no org data
// A user that is loaded and onboarded, but in an org that isn't onboarded, will not need a subscription
// A user that is loaded and onboarded, and in an org that is onboarded, will need a subscription

//Loading order to determine loading state: user -> org -> accounts, funds, contacts, org data -> transactions

//Modals that will manage the loading process:
// 1. Select org modal
// 2. Subscribe modal
// 3. Create org modal
// 4. Org onboarding modal

// Function to check if Auth email differs from user state (real-time detection)
const checkAuthEmailSync = async (authUser, db) => {
  try {
    // Get current user document from Firestore to compare
    const userDocRef = doc(db, `users/${authUser.uid}`);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();

      // If Auth email differs from stored Firestore email, sync it
      if (authUser.email !== userData.email) {
        await updateDoc(
          userDocRef,
          {
            email: authUser.email,
            emailSyncedAt: new Date().toISOString(),
          },
          { merge: true },
        );

        // Only show notification if sign-in sync hasn't already handled it
        if (!window.emailJustSyncedOnSignIn) {
          setTimeout(() => {
            alert(
              `Email successfully verified and updated to ${authUser.email}!`,
            );
          }, 500);
        }
      }
    }
  } catch (error) {
    console.error("Error in real-time email sync:", error);
  }
};

const UserProvider = ({ children }) => {
  // Set default date range to show recent transactions (last 2 years to future)
  const defaultStartDate = dayjs().subtract(2, "year").startOf("month");
  const defaultEndDate = dayjs().add(1, "year").endOf("month");

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({});
  const [org, setOrgRaw] = useState(null); //This is the org the user is currently viewing
  const setOrg = (newOrgId) => {
    setOrgRaw(newOrgId);
    if (newOrgId) {
      try { localStorage.setItem("lastUsedOrgId", newOrgId); } catch (e) { /* ignore */ }
    }
  };
  const [orgData, setOrgData] = useState(null); //This is the org subscription status and more
  const [subscriptionData, setSubscriptionData] = useState(null); // New: dedicated subscription state
  const [accounts, setAccounts] = useState([]);
  const [accountsHierarchy, setAccountsHierarchy] = useState({});
  const [funds, setFunds] = useState([]);
  const [fundsHierarchy, setFundsHierarchy] = useState({});
  const [labels, setLabels] = useState([]);
  const [labelsHierarchy, setLabelsHierarchy] = useState({});
  const [contacts, setContacts] = useState([]);
  const [dateRangeForTx, setDateRangeForTx] = useState({
    firstDate: defaultStartDate,
    lastDate: defaultEndDate,
  });
  const [loaded, setLoaded] = useState(false);
  // false | true | { recommend: "basic"|"plus"|"advanced", featureLabel?: string }
  // — truthy opens the subscribe modal; the object form pre-recommends a plan
  const [openSubscribe, setOpenSubscribe] = useState(false);
  const [selectOrgOpen, setSelectOrgOpen] = useState(false);
  const [monthlyAggregates, setMonthlyAggregates] = useState(null);
  const [orgRoles, setOrgRoles] = useState([]);
  //Delte queue is an array of objects that have the id of the transaction to delete, so the user can see their request to delete imedaiately as a strikethrough
  const [deleteQueue, setDeleteQueue] = useState([]);
  const [contactMapping, setContactMapping] = useState({});
  const [contactAccountFundMatches, setContactAccountFundMatches] = useState(
    {},
  );
  // GH#115: matches live in a per-contact subcollection now; the legacy
  // singleton doc (state above) is the fallback for un-backfilled orgs.
  // populated → per-contact lazy reads are authoritative; hasLookupData →
  // some contact clears the Tier-1 threshold, feeds predictionsReady.
  const [subMatchesPopulated, setSubMatchesPopulated] = useState(false);
  const [subMatchesHaveLookupData, setSubMatchesHaveLookupData] =
    useState(false);
  const [mlModelState, setMlModelState] = useState(null);
  // True when at least one expectedTransactions doc with status: "open"
  // exists for this org. Drives the Tier-0 branch of canRunPredictions
  // so the imported-tx form locks while the matcher is run server-side
  // even on brand-new orgs with no contact history yet.
  const [hasOpenExpectedTransactions, setHasOpenExpectedTransactions] =
    useState(false);
  const [features, setFeatures] = useState({});
  const [donors, setDonors] = useState([]);
  const [donationSources, setDonationSources] = useState([]);
  //Track whether we've notified free trialers so we don't notify them again
  const [freeTrialNotified, setFreeTrialNotified] = useState(false);
  const [openTrialNotification, setOpenTrialNotification] = useState(false);
  const [controlSessions, setControlSessions] = useState([]);

  // Multi-subscription billing data
  const [billingData, setBillingData] = useState(null);

  // Broken org detection state
  const [brokenOrgDetected, setBrokenOrgDetected] = useState(false);
  const [brokenOrgId, setBrokenOrgId] = useState(null);

  const authHook = useAuth();

  // NEW: Proper loading state tracking
  const [loadingState, setLoadingState] = useState({
    userChecked: false, // Has auth state been checked?
    userLoaded: false, // Is user data loaded (or confirmed no user)?
    orgSelected: false, // Has an org been selected?
    orgDataLoaded: false, // Is org metadata loaded?
    accountsLoaded: false, // Are accounts loaded?
    fundsLoaded: false, // Are funds loaded?
    contactsLoaded: false, // Are contacts loaded?
    // donationSources starts [] before its listener resolves, which is
    // indistinguishable from "org has no sources" — DonationSetupGuard must
    // wait on this flag instead of redirecting on the empty initial value
    // (GH#307).
    donationSourcesLoaded: false,
  });

  // NEW: State for handling no-org users and invites
  const [inviteCheckState, setInviteCheckState] = useState({
    checking: false, // Currently checking for invites
    checked: false, // Have we checked for invites?
    found: false, // Were invites found?
    waiting: false, // Waiting for invite to be processed
  });

  // Track when modal was manually closed to prevent immediate reopening
  const lastManualCloseRef = useRef(0);
  // Track if this is the initial org load for the user
  const initialOrgLoadRef = useRef(true);
  // Track last org entry to prevent duplicate tracking
  const lastTrackedOrgRef = useRef(null);
  const lastTrackTimeRef = useRef(0);
  // Track unsubscribe functions for cleanup
  const unsubscribeFunctionsRef = useRef({});
  // Per-contact prediction-match cache for fetchContactMatches (GH#115).
  // Short TTL: the old org-wide onSnapshot was live, so don't let a busy
  // data-entry session run on stale counts for long.
  const contactMatchesCacheRef = useRef({ org: null, byContact: {} });
  // Mirror controlSessions so the orgData onSnapshot closure can see the
  // current value (GH#117 guard would otherwise bail on take-control orgs
  // where the admin is not in data.users).
  const controlSessionsRef = useRef([]);

  // Helper function to update loading state
  const updateLoadingState = (updates) => {
    setLoadingState((prev) => ({ ...prev, ...updates }));
  };

  // Note: Loading state is now set directly in the listeners (lines 610, 647, 680)
  // when data arrives (even if empty). Loading state is reset to false when org
  // changes (line 1110-1116). No need for separate useEffects to track array length.

  useEffect(() => {
    if (org && orgData && !loadingState.orgDataLoaded) {
      updateLoadingState({ orgDataLoaded: true });
    } else if (org && !orgData && loadingState.orgDataLoaded) {
      updateLoadingState({ orgDataLoaded: false });
    }
  }, [orgData, org, loadingState.orgDataLoaded]);

  // Effective org roles, derived (not set inside the orgData snapshot). A
  // T3Admin/superAdmin who "takes control" of an org usually isn't in the
  // org's users map, so they have no member roles; while they hold an active
  // control session for this org we append an effective "admin" role so the
  // org-admin-gated UI (enter transactions, reconcile, contact/donor/fund/label
  // edit) is usable while supporting the org. Deriving from orgData +
  // controlSessions means the role is correct regardless of which Firestore
  // listener resolves first, and it clears the moment control is released or
  // the org changes (orgData -> null). See GH#181.
  useEffect(() => {
    setOrgRoles(
      computeEffectiveOrgRoles({
        orgData,
        controlSessions,
        org,
        uid: user?.uid,
      }),
    );
  }, [orgData, controlSessions, org, user]);

  // Compute if everything is loaded - now using actual state
  const isFullyLoaded = () => {
    const state = loadingState;

    // If no user, just need to have checked
    if (!user) {
      return state.userChecked;
    }

    // If user but no org selected yet, check invite state
    if (!org) {
      // If we're still checking for invites or waiting for them to be processed, not loaded
      if (inviteCheckState.checking || inviteCheckState.waiting) {
        return false;
      }

      // If we've checked and found no invites, then we're ready for user to create org
      if (inviteCheckState.checked && !inviteCheckState.found) {
        return state.userChecked && state.userLoaded;
      }

      // Default case - still checking
      return false;
    }

    // Basic requirements: user checked, user loaded, org selected, org data loaded
    const basicRequirements =
      state.userChecked &&
      state.userLoaded &&
      state.orgSelected &&
      state.orgDataLoaded &&
      !!orgData;

    if (!basicRequirements) {
      return false;
    }

    // If org is not onboarded, we only need org data - no accounts/funds/contacts required
    if (orgData && orgData.onboarded === false) {
      return true;
    }

    // If org is onboarded, then we need all the data
    const onboardedOrgRequirements =
      state.accountsLoaded &&
      state.fundsLoaded &&
      state.contactsLoaded &&
      // Double-check actual state has data
      accounts.length > 0 &&
      funds.length > 0 &&
      contacts.length > 0;

    return basicRequirements && onboardedOrgRequirements;
  };

  // Update loaded state based on loading state
  useEffect(() => {
    const shouldBeLoaded = isFullyLoaded();
    if (shouldBeLoaded !== loaded) {
      setLoaded(shouldBeLoaded);
    }
  }, [
    loadingState,
    user,
    org,
    loaded,
    accounts.length,
    funds.length,
    contacts.length,
    orgData,
  ]);

  // Helper function to track org entry
  const trackOrgEntry = async (orgId) => {
    // Prevent duplicate tracking within 5 seconds
    const now = Date.now();
    if (
      lastTrackedOrgRef.current === orgId &&
      now - lastTrackTimeRef.current < 5000
    ) {
      return;
    }

    if (authHook.functions && orgId && user && orgId !== org) {
      try {
        lastTrackedOrgRef.current = orgId;
        lastTrackTimeRef.current = now;

        const trackAuthEvent = tracedCallable(
          authHook.functions,
          "trackAuthEvent",
        );
        await trackAuthEvent({
          action: "entered_org",
          orgId: orgId,
          metadata: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString(),
            previousOrg: org || null,
          },
        });
      } catch (trackError) {
        console.error("Failed to track org entry:", trackError);
        // Don't block org loading if tracking fails
      }
    }
  };

  // Helper function to mark that modal was manually closed (for specific org)
  const markManualModalClose = () => {
    lastManualCloseRef.current = {
      timestamp: Date.now(),
      orgId: org,
    };
  };

  // Resolve the best fallback org: defaultOrgId → lastUsedOrgId → null
  // Returns null if neither default nor last-used is available, so the
  // org selector modal can be shown to first-time multi-org users.
  const resolveFallbackOrg = (data) => {
    const orgs = data?.orgs || [];
    if (orgs.length === 0) return null;

    const isValid = (id) => id && orgs.some((o) => o.orgID === id);

    if (isValid(data.defaultOrgId)) return data.defaultOrgId;

    try {
      const lastUsed = localStorage.getItem("lastUsedOrgId");
      if (isValid(lastUsed)) return lastUsed;
    } catch (e) { /* ignore */ }

    return null;
  };

  // Helper function to check if modal was recently closed manually (for THIS org)
  const wasRecentlyClosedManually = () => {
    if (!lastManualCloseRef.current) return false;

    const { timestamp, orgId } = lastManualCloseRef.current;

    // Only apply protection if it was THIS org that was closed
    if (orgId !== org) return false;

    return Date.now() - timestamp < 3000; // 3 second protection
  };

  // Helper function to check for invites for a user email
  const checkForInvites = async (userEmail) => {
    setInviteCheckState((prev) => ({ ...prev, checking: true }));

    // Check if we're in local development mode
    const isLocalDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    try {
      if (isLocalDev) {
        // In local dev, check Firestore directly
        const invitesQuery = query(
          collection(authHook.db, "invites"),
          where("email", "==", userEmail),
        );

        const invitesSnapshot = await getDocs(invitesQuery);
        const hasInvites = !invitesSnapshot.empty;

        setInviteCheckState({
          checking: false,
          checked: true,
          found: hasInvites,
          waiting: hasInvites,
        });

        return hasInvites;
      } else {
        // Use cloud function for production/deployed environments
        const checkInvitesForUser = tracedCallable(
          authHook.functions,
          "checkInvitesForUser",
        );
        const result = await checkInvitesForUser({ email: userEmail });

        const hasInvites = result.data.hasInvites;
        setInviteCheckState({
          checking: false,
          checked: true,
          found: hasInvites,
          waiting: hasInvites,
        });

        return hasInvites;
      }
    } catch (error) {
      console.error("Error checking for invites:", error);
      setInviteCheckState({
        checking: false,
        checked: true,
        found: false,
        waiting: false,
      });
      return false;
    }
  };

  // Helper to clean up org listeners
  const cleanupOrgListeners = () => {
    Object.values(unsubscribeFunctionsRef.current).forEach((unsub) => {
      if (typeof unsub === "function") unsub();
    });
    unsubscribeFunctionsRef.current = {};
  };

  // Reset every org-scoped slice back to its empty/null baseline. Shared by the
  // org-switch teardown and the access-revoked branch so BOTH clear the exact
  // same full set — otherwise a subset clear leaves the previous org's data
  // frozen on screen after its listeners are gone.
  const resetOrgScopedState = () => {
    setAccounts([]);
    setFunds([]);
    setAccountsHierarchy({});
    setFundsHierarchy({});
    setLabels([]);
    setLabelsHierarchy({});
    setContacts([]);
    setOrgData(null);
    setSubscriptionData(null);
    setMonthlyAggregates(null);
    setContactMapping({});
    setContactAccountFundMatches({});
    setDonationSources([]);
    // setLoadingState directly (not updateLoadingState) so this function stays
    // referentially stable for the effects that call it during teardown.
    setLoadingState((prev) => ({ ...prev, donationSourcesLoaded: false }));
  };

  // Extract listener setup into a separate function for reuse
  const setupOrgListeners = () => {
    if (!authHook?.db || !user || !org) return;
    // Idempotent: if the org listeners are already active, bail. Both callers
    // (the org-load effect and the 5s recovery) run cleanupOrgListeners first,
    // so this is a no-op for them; it only guards the take-control re-setup
    // path (#181) from double-subscribing regardless of effect ordering.
    if (unsubscribeFunctionsRef.current.orgData) return;

    // Every branch that will never receive a donationSources snapshot has to
    // settle the flag itself, or DonationSetupGuard spins forever.
    const settleDonationSourcesEmpty = () => {
      setDonationSources([]);
      updateLoadingState({ donationSourcesLoaded: true });
    };

    //Get the org data
    unsubscribeFunctionsRef.current.orgData = onSnapshot(
      doc(authHook.db, "orgs", org),
      (orgDoc) => {
        const data = orgDoc.data();

        if (data) {
          // A stale listener can fire after the user has been removed from
          // this org's users map (revoked access, role change, or a wrong
          // lastUsedOrgId). data.users[uid] is then undefined — accessing
          // .roles crashes the app. Bail and let cleanupOrgListeners run.
          // See GH#117.
          //
          // Exception: T3Admin/superAdmin take-control. The admin is not in
          // data.users on customer orgs but should still load orgData; we
          // detect that via an active adminControlSession for this org.
          const memberRecord = data.users?.[user.uid];
          const hasControlSession = controlSessionsRef.current.some(
            (s) => s.orgId === org,
          );
          if (!memberRecord && !hasControlSession) {
            // Access was revoked — mirror the org-switch teardown so the
            // previous org's data doesn't keep rendering (frozen at the last
            // snapshot) after its listeners are torn down. Clear the FULL
            // org-scoped set, not just a subset.
            cleanupOrgListeners();
            setOrgRoles([]);
            resetOrgScopedState();
            return;
          }
          setOrgData(data);
          // orgRoles (including the take-control effective-admin grant) is
          // derived from orgData + controlSessions in a dedicated effect below,
          // so it stays correct regardless of which listener resolves first and
          // clears when control is released. See the orgRoles effect (#181).
          Sentry.setTag("orgId", org);

          if (data.features) {
            setFeatures(data.features);
            //if features.donors is true, fetch the donors
            if (data.features.donorManagement) {
              // Load donors from collection (orgs/{orgId}/donors)
              // Filter out merged donors (those with mergedInto field)
              // This orgData snapshot re-fires on every org doc change, so
              // unsubscribe the previous listener before reassigning —
              // otherwise a leaked org-A listener keeps injecting org-A data
              // while org B is active.
              if (unsubscribeFunctionsRef.current.donors) {
                unsubscribeFunctionsRef.current.donors();
              }
              unsubscribeFunctionsRef.current.donors = onSnapshot(
                collection(authHook.db, "orgs", org, "donors"),
                (snapshot) => {
                  const donorsData = snapshot.docs
                    .map((doc) => {
                      const data = doc.data();
                      return {
                        ...data,
                        id: Array.isArray(data.id) ? data.id : [doc.id],
                        firestoreId: doc.id,
                      };
                    })
                    .filter((donor) => !donor.mergedInto); // Exclude merged donors
                  setDonors(donorsData);
                },
                (error) => {
                  // Permission errors are expected when access is revoked
                  // mid-session — silence them
                  if (error?.code !== "permission-denied") {
                    Sentry.withScope((scope) => {
                      scope.setTag("component", "context_and_index");
                      scope.setTag("action", "donors_listener");
                      scope.setTag("org_id", org);
                      Sentry.captureException(error);
                    });
                  }
                  setDonors([]);
                },
              );
              // Load donation sources
              if (unsubscribeFunctionsRef.current.donationSources) {
                unsubscribeFunctionsRef.current.donationSources();
              }
              unsubscribeFunctionsRef.current.donationSources = onSnapshot(
                doc(authHook.db, "orgs", org, "donorManagement", "donationSources"),
                (sourcesDoc) => {
                  if (sourcesDoc.exists()) {
                    const data = sourcesDoc.data();
                    const sourcesData = data.sources || [];
                    setDonationSources(sourcesData);
                    updateLoadingState({ donationSourcesLoaded: true });
                  } else {
                    settleDonationSourcesEmpty();
                  }
                },
                (error) => {
                  // Permission errors are expected when access is revoked
                  // mid-session — silence them
                  if (error?.code !== "permission-denied") {
                    Sentry.withScope((scope) => {
                      scope.setTag("component", "context_and_index");
                      scope.setTag("action", "donation_sources_listener");
                      scope.setTag("org_id", org);
                      Sentry.captureException(error);
                    });
                  }
                  // Fail OPEN: mark loaded even on error so DonationSetupGuard
                  // redirects to setup instead of spinning forever.
                  settleDonationSourcesEmpty();
                },
              );
            } else {
              setDonors([]);
              settleDonationSourcesEmpty();
            }

            // Labels load regardless of the entitlement (§6.3): committed
            // label names must keep rendering read-only after a downgrade.
            // Create/edit/assign surfaces gate on features.labels themselves.
            if (unsubscribeFunctionsRef.current.labels) {
              unsubscribeFunctionsRef.current.labels();
            }
            unsubscribeFunctionsRef.current.labels = onSnapshot(
              collection(authHook.db, "orgs", org, "labels"),
              (snapshot) => {
                const newLabels = snapshot.docs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }));
                setLabels(newLabels);
              },
            );

            if (unsubscribeFunctionsRef.current.labelsHierarchy) {
              unsubscribeFunctionsRef.current.labelsHierarchy();
            }
            unsubscribeFunctionsRef.current.labelsHierarchy = onSnapshot(
              doc(
                authHook.db,
                "orgs",
                org,
                "organizationAndSettings",
                "labelsHierarchy",
              ),
              (doc) => {
                if (doc.data() !== undefined) {
                  setLabelsHierarchy(doc.data());
                } else {
                  setLabelsHierarchy({});
                }
              },
            );
          } else {
            setFeatures({});
            setDonors([]);
            settleDonationSourcesEmpty();
            setLabels([]);
            setLabelsHierarchy({});
          }
        } else {
          // Org doc missing — no donation-sources listener will ever run, so
          // settle the flag here rather than leaving consumers spinning.
          settleDonationSourcesEmpty();
        }
      },
    );

    // Get subscription data from new location
    unsubscribeFunctionsRef.current.subscription = onSnapshot(
      doc(authHook.db, "orgs", org, "organizationAndSettings", "subscription"),
      (subDoc) => {
        const subData = subDoc.data();
        setSubscriptionData(subData);
        if (subData?.status) {
          Sentry.setTag("subscriptionStatus", subData.status);
        }

        if (subData) {
          // Also update orgData for backward compatibility
          setOrgData((prevOrgData) => ({
            ...prevOrgData,
            subscriptionData: subData,
            subscriptionCurrent: subData.subscriptionCurrent,
          }));
        }
      },
    );

    //Fetch Accounts
    unsubscribeFunctionsRef.current.accounts = onSnapshot(
      collection(authHook.db, "orgs", org, "accounts"),
      (snapshot) => {
        const newAccounts = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setAccounts(newAccounts);
        updateLoadingState({ accountsLoaded: true });
      },
    );

    //Fetch Account Hierarchy
    unsubscribeFunctionsRef.current.accountsHierarchy = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "accountsHierarchy",
      ),
      (doc) => {
        if (doc.data() !== undefined) {
          setAccountsHierarchy(doc.data());
        } else {
          setAccountsHierarchy({});
        }
      },
    );

    //Fetch the funds
    unsubscribeFunctionsRef.current.funds = onSnapshot(
      collection(authHook.db, "orgs", org, "funds"),
      (snapshot) => {
        const newFunds = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setFunds(newFunds);
        updateLoadingState({ fundsLoaded: true });
      },
    );

    //Fetch Fund Hierarchy
    unsubscribeFunctionsRef.current.fundsHierarchy = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "fundsHierarchy",
      ),
      (doc) => {
        if (doc.data() !== undefined) {
          setFundsHierarchy(doc.data());
        } else {
          setFundsHierarchy({});
        }
      },
    );

    //Fetch the contacts
    // Filter out merged contacts (those with mergedInto field)
    unsubscribeFunctionsRef.current.contacts = onSnapshot(
      collection(authHook.db, "orgs", org, "contacts"),
      (snapshot) => {
        const newContacts = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
          .filter((c) => !c.mergedInto);
        setContacts(newContacts);
        updateLoadingState({ contactsLoaded: true });
      },
    );

    //Fetch all the monthly aggregate docs from the monthlyAggregates collection
    unsubscribeFunctionsRef.current.monthlyAggregates = onSnapshot(
      collection(authHook.db, "orgs", org, "monthlyAggregates"),
      (snapshot) => {
        const monthlyAggregates = {};
        snapshot.docs.forEach((doc) => {
          monthlyAggregates[doc.id] = doc.data();
        });
        setMonthlyAggregates(monthlyAggregates);
      },
    );

    //Fetch contact account fund matches for predictions — legacy singleton
    // doc, kept as the fallback until every org is backfilled into the
    // subcollection (GH#115).
    unsubscribeFunctionsRef.current.contactAccountFundMatches = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "contactAccountFundMatches",
      ),
      (doc) => {
        if (doc.exists()) {
          setContactAccountFundMatches(doc.data());
        } else {
          setContactAccountFundMatches({});
        }
      },
    );

    // GH#115: subcollection probes, limit(1) each — "is it backfilled?"
    // and "does any contact clear the Tier-1 threshold?". Deliberately NOT
    // an org-wide subscription: a large org has thousands of contact docs
    // and this context must never preload them (per-contact reads happen
    // lazily at prediction time via fetchContactMatches).
    unsubscribeFunctionsRef.current.contactMatchesPopulated = onSnapshot(
      query(
        collection(authHook.db, "orgs", org, "contactAccountFundMatches"),
        limit(1),
      ),
      (snapshot) => {
        setSubMatchesPopulated(!snapshot.empty);
      },
    );
    unsubscribeFunctionsRef.current.contactMatchesLookupReady = onSnapshot(
      query(
        collection(authHook.db, "orgs", org, "contactAccountFundMatches"),
        where("maxMatchCount", ">=", LOOKUP_PREDICTION_MIN_COUNT),
        limit(1),
      ),
      (snapshot) => {
        setSubMatchesHaveLookupData(!snapshot.empty);
      },
    );

    // Fetch ML model state for prediction readiness awareness
    unsubscribeFunctionsRef.current.mlModelState = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "mlModelState",
      ),
      (doc) => {
        if (doc.exists()) {
          setMlModelState(doc.data());
        } else {
          setMlModelState(null);
        }
      },
    );

    // Watch for any open expectedTransactions doc — drives the Tier-0
    // branch of canRunPredictions. limit(1) is sufficient because we
    // only care about the boolean "any open?". Index is automatic
    // (single-field equality on status).
    unsubscribeFunctionsRef.current.expectedTransactions = onSnapshot(
      query(
        collection(authHook.db, "orgs", org, "expectedTransactions"),
        where("status", "==", "open"),
        limit(1),
      ),
      (snapshot) => {
        setHasOpenExpectedTransactions(!snapshot.empty);
      },
    );
  };

  // Load the user's data
  useEffect(() => {
    if (authHook.auth && !authHook.isLoadingFirebase) {
      const unsubscribe = onAuthStateChanged(authHook.auth, async (user) => {

        if (user) {
          // Check for email verification completion (real-time detection)
          await checkAuthEmailSync(user, authHook.db);

          setUser(user);
          Sentry.setUser({ id: user.uid, email: user.email });
          updateLoadingState({ userChecked: true });
        } else {
          // User logged out - release any active control sessions (T3Admins only)
          // Check if user is a T3Admin by checking the admins/permissions document
          if (authHook.functions && authHook.db && user) {
            try {
              // Only check for control sessions if user might be a T3Admin
              // This avoids unnecessary calls for 99% of users
              const permissionsRef = doc(authHook.db, "admins", "permissions");
              const permissionsSnap = await getDoc(permissionsRef);

              if (permissionsSnap.exists()) {
                const permissionsData = permissionsSnap.data();
                const isT3Admin =
                  permissionsData.T3Admins &&
                  permissionsData.T3Admins.includes(user.uid);
                const isSuperAdmin =
                  permissionsData.superAdmins &&
                  permissionsData.superAdmins.includes(user.uid);

                // Only fetch and release control sessions if user is a system admin
                if (isT3Admin || isSuperAdmin) {
                  const getMyControlSessions = tracedCallable(
                    authHook.functions,
                    "getMyControlSessions",
                  );
                  const result = await getMyControlSessions();

                  if (result.data.success && result.data.sessions.length > 0) {
                    // Release all active control sessions
                    const releaseControlOfOrg = tracedCallable(
                      authHook.functions,
                      "releaseControlOfOrg",
                    );

                    await Promise.all(
                      result.data.sessions.map(async (session) => {
                        try {
                          await releaseControlOfOrg({ orgId: session.orgId });
                        } catch (err) {
                          console.error(
                            `Failed to release control of org ${session.orgName}:`,
                            err,
                          );
                        }
                      }),
                    );
                  }
                }
              }
            } catch (error) {
              // Don't block logout if check/release fails
            }
          }

          setUser(null);
          setUserData({});
          Sentry.setUser(null);
          setOrg(null);
          try { localStorage.removeItem("lastUsedOrgId"); } catch (e) { /* ignore */ }
          // NEW: Clear all org-related state on logout to prevent inheritance by new users
          resetOrgScopedState();
          setOrgRoles([]);
          setFeatures({});
          setDonors([]);
          setDeleteQueue([]);
          setControlSessions([]);
          cleanupOrgListeners();
          updateLoadingState({
            userChecked: true,
            userLoaded: true,
            orgSelected: false,
            orgDataLoaded: false,
            accountsLoaded: false,
            fundsLoaded: false,
            contactsLoaded: false,
          });
        }
      });
      return () => {
        unsubscribe();
      };
    }
  }, [authHook, authHook?.auth, authHook?.isLoadingFirebase]);

  // Load the user's orgs, and check for more than one org
  useEffect(() => {
    if (authHook && !authHook.isLoadingFirebase && user) {
      const unsub = onSnapshot(
        doc(authHook.db, "users", user.uid),
        async (doc) => {
          const data = doc.data();

          // Safety check: ensure userData is never completely undefined
          setUserData(data || {});

          // Only update userLoaded if it's not already true to prevent repeated updates
          if (!loadingState.userLoaded) {
            updateLoadingState({ userLoaded: true });
          }

          if (data && data.orgs && data.orgs.length > 0) {
            // User has orgs - stop waiting for invites if we were
            if (inviteCheckState.waiting) {
              setInviteCheckState({
                checking: false,
                checked: true,
                found: true,
                waiting: false,
              });
            }

            //Check if the user is in more than one org
            if (data.orgs.length > 1) {
              if (!wasRecentlyClosedManually()) {
                // Check if user just logged in after logging out
                // If so, use default org instead of URL org
                const justLoggedOut = sessionStorage.getItem("justLoggedOut");
                if (justLoggedOut) {
                  sessionStorage.removeItem("justLoggedOut");

                  // Clear URL org parameter if present
                  const urlParams = new URLSearchParams(window.location.search);
                  if (urlParams.has("org")) {
                    urlParams.delete("org");
                    const newUrl = urlParams.toString()
                      ? `${window.location.pathname}?${urlParams.toString()}`
                      : window.location.pathname;
                    window.history.replaceState({}, "", newUrl);
                  }

                  // Use default or last-used org if available
                  setTimeout(() => {
                    if (!org) {
                      const resolvedOrg = resolveFallbackOrg(data);
                      if (resolvedOrg) {
                        setOrg(resolvedOrg);
                      } else {
                        setOrg(data.orgs[0].orgID);
                        setSelectOrgOpen(true);
                      }
                    }
                  }, 250);
                  return; // Skip normal URL handling
                }

                // Check if URL has an org parameter - if so, let URL manager handle it
                const urlParams = new URLSearchParams(window.location.search);
                const urlOrgId = urlParams.get("org");

                if (!urlOrgId) {
                  // No URL org parameter, proceed with fallback logic
                  setTimeout(() => {
                    if (!org) {
                      const resolvedOrg = resolveFallbackOrg(data);
                      if (resolvedOrg) {
                        setOrg(resolvedOrg);
                      } else {
                        setOrg(data.orgs[0].orgID);
                        setSelectOrgOpen(true);
                      }
                    }
                  }, 250);
                }
              }
            } else if (data.orgs.length === 1) {
              //Set the user's org to the only org they are in
              const newOrgId = data.orgs[0].orgID;
              if (newOrgId !== org) {
                setOrg(newOrgId);
                // Track org entry for single org users on initial load
                if (initialOrgLoadRef.current) {
                  setTimeout(() => {
                    trackOrgEntry(newOrgId);
                    initialOrgLoadRef.current = false;
                  }, 1000);
                }
              }
            }
          } else {
            // User has no orgs - check for invites if we haven't already
            if (!inviteCheckState.checked && !inviteCheckState.checking) {
              const userEmail = data?.email || user?.email;

              if (!userEmail) {
                setInviteCheckState({
                  checking: false,
                  checked: true,
                  found: false,
                  waiting: false,
                });
                updateLoadingState({
                  orgSelected: true,
                  orgDataLoaded: true,
                  accountsLoaded: true,
                  fundsLoaded: true,
                  contactsLoaded: true,
                });
                return;
              }

              const hasInvites = await checkForInvites(userEmail);

              if (!hasInvites) {
                updateLoadingState({
                  orgSelected: true,
                  orgDataLoaded: true,
                  accountsLoaded: true,
                  fundsLoaded: true,
                  contactsLoaded: true,
                });
              }
            } else if (inviteCheckState.checked && !inviteCheckState.found) {
              updateLoadingState({
                orgSelected: true,
                orgDataLoaded: true,
                accountsLoaded: true,
                fundsLoaded: true,
                contactsLoaded: true,
              });
            }
          }
        },
      );
      return () => unsub();
    }
  }, [
    authHook,
    authHook.db,
    user,
    inviteCheckState.checked,
    inviteCheckState.checking,
  ]);

  // Load control sessions for T3Admins/superAdmins
  useEffect(() => {
    if (!authHook?.db || !authHook?.functions || !user) {
      setControlSessions([]);
      return;
    }

    // Real-time listener for active control sessions
    const sessionsQuery = query(
      collection(authHook.db, "adminControlSessions"),
      where("t3AdminUid", "==", user.uid),
      where("endTime", "==", null),
    );

    const unsubscribe = onSnapshot(
      sessionsQuery,
      (snapshot) => {
        const sessions = snapshot.docs.map((doc) => ({
          sessionId: doc.id,
          ...doc.data(),
        }));
        controlSessionsRef.current = sessions;
        setControlSessions(sessions);
      },
      (error) => {
        // Permission errors are expected for non-admin users — silence them
        if (error?.code !== "permission-denied") {
          console.error("Error fetching control sessions:", error);
        }
        setControlSessions([]);
      },
    );

    return () => unsubscribe();
  }, [authHook?.db, authHook?.functions, user?.uid]);

  // Lock org to controlled org when admin has active control sessions (fixes #88)
  // On page refresh, controlSessions loads after the initial org resolution,
  // so we need to correct the org once sessions are known.
  useEffect(() => {
    if (controlSessions.length > 0) {
      const controlledOrgId = controlSessions[0].orgId;
      if (org !== controlledOrgId) {
        setOrg(controlledOrgId);
      }
      // Close org selector if it was opened during the race
      if (selectOrgOpen) {
        setSelectOrgOpen(false);
      }
    }
  }, [controlSessions]);

  // Load billing account data for multi-subscription management
  useEffect(() => {
    if (!authHook?.db || !user) {
      setBillingData(null);
      return;
    }

    // Listen for billing account data changes
    const unsubscribe = onSnapshot(
      doc(authHook.db, "billingAccounts", user.uid),
      (snapshot) => {
        if (snapshot.exists()) {
          setBillingData(snapshot.data());
        } else {
          setBillingData(null);
        }
      },
      (error) => {
        // Permission errors are expected for users without billing accounts — silence them
        if (error?.code !== "permission-denied") {
          console.error("Error fetching billing account data:", error);
        }
        setBillingData(null);
      },
    );

    return () => unsubscribe();
  }, [authHook?.db, user?.uid]);

  // React to URL changes for multi-org users - fixes intermittent loading hang
  useEffect(() => {
    if (
      user &&
      userData?.orgs &&
      userData.orgs.length > 1 &&
      !org &&
      !wasRecentlyClosedManually()
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlOrgId = urlParams.get("org");

      if (!urlOrgId) {
        setTimeout(() => {
          if (!org) {
            const resolvedOrg = resolveFallbackOrg(userData);
            if (resolvedOrg) {
              setOrg(resolvedOrg);
            } else {
              setOrg(userData.orgs[0].orgID);
              setSelectOrgOpen(true);
            }
          }
        }, 100);
      }
    }
  }, [user, userData, org, window.location.search]);

  // Enhanced setOrg that includes tracking
  const setOrgWithTracking = (newOrgId) => {
    if (newOrgId && newOrgId !== org) {
      markManualModalClose(); // Prevent modal from reopening
      setOrg(newOrgId);
      trackOrgEntry(newOrgId);
    } else {
      setOrg(newOrgId);
    }
  };

  // Handle org changes
  useEffect(() => {
    if (!org) return;

    // Clean up previous listeners only on org change
    cleanupOrgListeners();

    // Reset data
    resetOrgScopedState();

    // Reset loading states for org data - arrays will be watched by useEffects above
    updateLoadingState({
      orgSelected: true,
      orgDataLoaded: false,
      accountsLoaded: false,
      fundsLoaded: false,
      contactsLoaded: false,
    });
  }, [org]);

  // Recovery mechanism: Re-setup listeners if data doesn't arrive within reasonable time
  useEffect(() => {
    if (!org || !authHook?.db) return;

    const timeoutId = setTimeout(() => {
      // If we still don't have data after 5 seconds, try to re-setup listeners
      if (
        !loadingState.accountsLoaded ||
        !loadingState.fundsLoaded ||
        !loadingState.contactsLoaded ||
        !loadingState.orgDataLoaded
      ) {
        cleanupOrgListeners();
        setupOrgListeners();
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [
    org,
    authHook?.db,
    loadingState.accountsLoaded,
    loadingState.fundsLoaded,
    loadingState.contactsLoaded,
    loadingState.orgDataLoaded,
  ]);

  // Broken org detection: Separate from normal loading check
  // This detects when Firestore RETURNS empty data (corruption), not just waiting for data
  // Normal loading (`loaded` state) checks if data exists - this checks if data is MISSING after load attempt
  useEffect(() => {
    if (!org || !orgData) return;

    // Skip un-onboarded orgs (they legitimately have no data yet)
    if (orgData.onboarded === false) return;

    // CRITICAL: Only check when Firestore listeners have fired and returned data (or empty)
    // loadingState.accountsLoaded means "we got a response from Firestore" (even if empty)
    // This is different from loaded=true which requires data.length > 0
    if (
      !loadingState.accountsLoaded ||
      !loadingState.fundsLoaded ||
      !loadingState.contactsLoaded
    ) {
      return;
    }

    // If normal loading succeeded, don't show broken modal (app is working)
    if (loaded) {
      // Clear broken state if it was set
      if (brokenOrgDetected) {
        setBrokenOrgDetected(false);
        setBrokenOrgId(null);
      }
      return;
    }

    // Don't re-show modal if user manually closed it recently (for THIS specific org)
    if (wasRecentlyClosedManually()) {
      return;
    }

    // CRITICAL: Check if org document exists at all
    if (!orgData) {
      console.error("Broken org detected: Org document does not exist", org);
      setBrokenOrgDetected(true);
      setBrokenOrgId(org);
      return; // Early return - no need to check child data
    }

    // Skip check for un-onboarded orgs (they're still being set up)
    if (orgData.onboarded === false) {
      setBrokenOrgDetected(false);
      setBrokenOrgId(null);
      return;
    }

    // Check if org is missing required child data
    const isMissingAccounts = accounts.length === 0;
    const isMissingFunds = funds.length === 0;
    const isMissingContacts = contacts.length === 0;
    const isMissingAccountsHierarchy =
      !accountsHierarchy || Object.keys(accountsHierarchy).length === 0;
    const isMissingFundsHierarchy =
      !fundsHierarchy || Object.keys(fundsHierarchy).length === 0;

    if (
      isMissingAccounts ||
      isMissingFunds ||
      isMissingContacts ||
      isMissingAccountsHierarchy ||
      isMissingFundsHierarchy
    ) {
      console.error("Broken org detected: Missing required child data", org);
      // Set broken org state - BrokenOrgModal will open based on brokenOrgDetected
      setBrokenOrgDetected(true);
      setBrokenOrgId(org);
    } else {
      // Org is healthy, clear broken state
      setBrokenOrgDetected(false);
      setBrokenOrgId(null);
    }
  }, [
    org,
    orgData,
    loaded,  // Cancel broken check if normal loading succeeds
    loadingState.accountsLoaded,
    loadingState.fundsLoaded,
    loadingState.contactsLoaded,
    brokenOrgDetected,  // Need this to clear state when loaded becomes true
    // Note: Data arrays NOT in deps to prevent re-checks during loading
    // Note: Uses `loaded` to cancel detection if normal loading succeeds
  ]);

  // Load the org's accounts, funds, contacts, and org data
  useEffect(() => {
    if (authHook && !authHook.isLoadingFirebase && user && org) {
      setupOrgListeners();

      // DON'T cleanup listeners here - they should stay active!
      // Cleanup only happens when org changes (in the org change useEffect)
    }
  }, [authHook.db, user, org, authHook]);

  // Take-control re-setup (GH#181). The orgData snapshot can resolve before the
  // control-sessions listener loads; for a T3Admin with no membership it then
  // hits the no-membership teardown at the top of the orgData snapshot, which
  // runs cleanupOrgListeners() and leaves orgData null. When the control
  // session subsequently arrives, re-establish the org listeners immediately
  // instead of waiting for the 5s recovery timer. setupOrgListeners is
  // idempotent (no-ops if orgData is already subscribed), so this is safe even
  // when it runs alongside the org-load effect above.
  useEffect(() => {
    if (!authHook?.db || !user || !org) return;
    const hasControlSessionForOrg = controlSessions.some(
      (s) => s.orgId === org,
    );
    if (hasControlSessionForOrg && !unsubscribeFunctionsRef.current.orgData) {
      setupOrgListeners();
    }
  }, [controlSessions, org, authHook?.db, user]);

  // GH#115: lazy per-contact prediction-match read. Subcollection doc when
  // the org is backfilled, legacy map entry otherwise. Returns
  // { accounts, funds, mlPrediction? } or null. 60s per-contact cache —
  // fresh enough for data entry without re-reading on every keystroke.
  const CONTACT_MATCHES_CACHE_TTL_MS = 60_000;
  const fetchContactMatches = async (contactId) => {
    if (!contactId || !org || !authHook?.db) return null;

    const cache = contactMatchesCacheRef.current;
    if (cache.org !== org) {
      cache.org = org;
      cache.byContact = {};
    }
    const cached = cache.byContact[contactId];
    if (cached && Date.now() - cached.fetchedAt < CONTACT_MATCHES_CACHE_TTL_MS) {
      return cached.matches;
    }

    // The subcollection is PARTIAL until this org's backfill runs (the
    // tracker only creates docs for contacts touched since deploy, and an
    // ML run can create prediction-only docs with no count history), so a
    // sub doc only shadows the legacy map entry when it actually carries
    // counts. Mirrors readContactMatches in functions/transactions/shared/
    // contactMatches.js.
    const legacyEntry = contactAccountFundMatches?.[contactId] || null;
    let matches = legacyEntry;
    if (subMatchesPopulated) {
      try {
        const snap = await getDoc(
          doc(authHook.db, "orgs", org, "contactAccountFundMatches", contactId),
        );
        if (snap.exists()) {
          const subData = snap.data();
          const hasCounts =
            Object.keys(subData.accounts || {}).length > 0 ||
            Object.keys(subData.funds || {}).length > 0;
          if (hasCounts || !legacyEntry) {
            matches = subData;
          } else if (subData.mlPrediction) {
            matches = { ...legacyEntry, mlPrediction: subData.mlPrediction };
          }
        }
      } catch (error) {
        // Keep the legacy fallback rather than losing predictions.
      }
    }

    cache.byContact[contactId] = { matches, fetchedAt: Date.now() };
    return matches;
  };

  // Legacy compatibility properties
  const isParentDataLoaded = loadingState.userLoaded;
  const isChildDataLoaded =
    loadingState.orgDataLoaded &&
    loadingState.accountsLoaded &&
    loadingState.fundsLoaded &&
    loadingState.contactsLoaded &&
    // Also check actual data exists
    accounts.length > 0 &&
    funds.length > 0 &&
    contacts.length > 0 &&
    !!orgData;
  const userStillLoading = !loadingState.userChecked;

  // Reset invite check state when user changes
  useEffect(() => {
    setInviteCheckState({
      checking: false,
      checked: false,
      found: false,
      waiting: false,
    });
  }, [user?.uid]);

  // Single-fund mode: orgs with exactly one usable fund get fund inputs
  // hidden/auto-filled and reports default to Total columns. Purely derived
  // from the live funds listener, so adding a second fund flips this off
  // everywhere without a reload. isUsableFund is the same predicate the fund
  // pickers filter by, so "one fund in dropdowns" and "single-fund mode" can
  // never disagree. Zero funds (mid-onboarding) is NOT single-fund mode.
  const activeFunds = useMemo(() => funds.filter(isUsableFund), [funds]);
  const isSingleFundOrg = activeFunds.length === 1;
  const defaultFund = isSingleFundOrg ? activeFunds[0] : null;

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userData,
        setUserData,
        org,
        setOrg: setOrgWithTracking,
        orgData,
        subscriptionData,
        orgRoles,
        accounts,
        funds,
        setFunds,
        isSingleFundOrg,
        defaultFund,
        labels,
        setLabels,
        contacts,
        accountsHierarchy,
        fundsHierarchy,
        setFundsHierarchy,
        labelsHierarchy,
        setLabelsHierarchy,
        dateRangeForTx,
        setDateRangeForTx,
        loaded,
        setIsChildDataLoaded: () => {}, // No-op for legacy compatibility
        userStillLoading,
        setOpenSubscribe,
        openSubscribe,
        selectOrgOpen,
        setSelectOrgOpen,
        markManualModalClose,
        isParentDataLoaded,
        setAccounts,
        setAccountsHierarchy,
        setContacts,
        monthlyAggregates,
        setUserStillLoading: () => {}, // No-op for legacy compatibility
        deleteQueue,
        setDeleteQueue,
        contactMapping,
        contactAccountFundMatches,
        fetchContactMatches,
        mlModelState,
        // GH#115: subcollection readiness OR the legacy-map gates — an org
        // is ready if either home says it has usable Tier-1 data.
        predictionsReady:
          subMatchesHaveLookupData ||
          canRunPredictions(
            orgData,
            mlModelState,
            contactAccountFundMatches,
            hasOpenExpectedTransactions,
          ),
        features,
        donors,
        donationSources,
        donationSourcesLoaded: loadingState.donationSourcesLoaded,
        freeTrialNotified,
        setFreeTrialNotified,
        openTrialNotification,
        setOpenTrialNotification,
        authHook,
        inviteCheckState,
        controlSessions,
        billingData,
        brokenOrgDetected,
        setBrokenOrgDetected,
        brokenOrgId,
      }}>
      <ThemeUserSync />
      <TransactionHookRegistryProvider>
        {children}
      </TransactionHookRegistryProvider>
    </UserContext.Provider>
  );
};

export default UserProvider;
